import { useQuery, useMutation } from '@apollo/client';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { useState, useMemo } from 'react';

import {
  WORKSPACE_MEMBERS_QUERY,
  WORKSPACE_INVITATIONS_QUERY,
  INVITE_TO_WORKSPACE_MUTATION,
  REMOVE_WORKSPACE_MEMBER_MUTATION,
  RESEND_WORKSPACE_INVITE_MUTATION,
} from '../services/graphql';
import { useWorkspace, WorkspaceMember } from '../contexts/WorkspaceContext';
import type { WorkspaceInvitation } from '../types';

type TabValue = 'members' | 'invitations';

export default function Members() {
  const { currentWorkspace, isOwner } = useWorkspace();
  const [tab, setTab] = useState<TabValue>('members');
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLinkDialogOpen, setInviteLinkDialogOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<WorkspaceMember | null>(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const workspaceId = currentWorkspace?.id;

  // Fetch members
  const {
    data: membersData,
    loading: membersLoading,
    refetch: refetchMembers,
  } = useQuery(WORKSPACE_MEMBERS_QUERY, {
    variables: { workspaceId },
    skip: !workspaceId || !isOwner,
    fetchPolicy: 'cache-and-network',
  });

  // Fetch invitations
  const {
    data: invitationsData,
    loading: invitationsLoading,
    refetch: refetchInvitations,
  } = useQuery(WORKSPACE_INVITATIONS_QUERY, {
    variables: { workspaceId },
    skip: !workspaceId || !isOwner,
    fetchPolicy: 'cache-and-network',
  });

  const [inviteToWorkspace, { loading: inviteLoading }] = useMutation(INVITE_TO_WORKSPACE_MUTATION, {
    onCompleted: (data) => {
      if (data.inviteToWorkspace.success) {
        if (data.inviteToWorkspace.inviteLink) {
          setInviteLink(data.inviteToWorkspace.inviteLink);
          setInviteLinkDialogOpen(true);
        }
        setInviteDialogOpen(false);
        setInviteEmail('');
        refetchInvitations();
        setSuccess('Invitation sent successfully');
      } else {
        setError(data.inviteToWorkspace.message || 'Failed to send invitation');
      }
    },
    onError: (err) => setError(err.message),
  });

  const [removeWorkspaceMember, { loading: removeLoading }] = useMutation(
    REMOVE_WORKSPACE_MEMBER_MUTATION,
    {
      onCompleted: (data) => {
        if (data.removeWorkspaceMember.success) {
          setSuccess('Member removed successfully');
          setDeleteDialogOpen(false);
          setMemberToRemove(null);
          refetchMembers();
        } else {
          setError(data.removeWorkspaceMember.message || 'Failed to remove member');
        }
      },
      onError: (err) => setError(err.message),
    }
  );

  const [resendInvite, { loading: resendLoading }] = useMutation(RESEND_WORKSPACE_INVITE_MUTATION, {
    onCompleted: (data) => {
      if (data.resendWorkspaceInvite.success) {
        setSuccess('Invitation resent successfully');
        refetchInvitations();
      } else {
        setError(data.resendWorkspaceInvite.message || 'Failed to resend invitation');
      }
    },
    onError: (err) => setError(err.message),
  });

  const members: WorkspaceMember[] = membersData?.workspaceMembers || [];
  const invitations: WorkspaceInvitation[] = invitationsData?.workspaceInvitations || [];

  // Filter only pending invitations
  const pendingInvitations = useMemo(() => {
    return invitations.filter((inv) => inv.status === 'pending');
  }, [invitations]);

  const filteredMembers = useMemo(() => {
    return members.filter((member) =>
      member.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [members, searchQuery]);

  const filteredInvitations = useMemo(() => {
    return pendingInvitations.filter((inv) =>
      inv.invitedEmail.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [pendingInvitations, searchQuery]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      setError('Please enter an email address');
      return;
    }
    if (!workspaceId) return;

    await inviteToWorkspace({
      variables: {
        workspaceId,
        email: inviteEmail.trim(),
      },
    });
  };

  const handleRemoveClick = (member: WorkspaceMember) => {
    setMemberToRemove(member);
    setDeleteDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!memberToRemove || !workspaceId) return;
    await removeWorkspaceMember({
      variables: {
        workspaceId,
        userId: memberToRemove.userId,
      },
    });
  };

  const handleResendInvite = async (invitationId: string) => {
    await resendInvite({
      variables: { invitationId },
    });
  };

  // Show empty state if not owner
  if (!isOwner) {
    return (
      <Box sx={{ flex: 1 }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <PeopleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Access Restricted
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Only workspace owners can manage members.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1 }}>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Members
            </Typography>
            {currentWorkspace && (
              <Typography variant="body2" color="text.secondary">
                Manage members of "{currentWorkspace.name}"
              </Typography>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Controls */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite Member
          </Button>

          <TextField
            placeholder="Search by email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label={`Members (${members.length})`} value="members" />
          <Tab label={`Pending Invites (${pendingInvitations.length})`} value="invitations" />
        </Tabs>

        {/* Members Tab */}
        {tab === 'members' && (
          <>
            {membersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredMembers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <PeopleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {members.length === 0 ? 'No members yet' : 'No members match your search'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {members.length === 0 && 'Invite your first team member to get started'}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMembers.map((member) => (
                      <TableRow key={member.id} hover>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={member.role}
                            size="small"
                            color={member.role === 'OWNER' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(member.joinedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          {member.role !== 'OWNER' && (
                            <Tooltip title="Remove member">
                              <IconButton
                                edge="end"
                                onClick={() => handleRemoveClick(member)}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* Invitations Tab */}
        {tab === 'invitations' && (
          <>
            {invitationsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredInvitations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <SendIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {pendingInvitations.length === 0
                    ? 'No pending invitations'
                    : 'No invitations match your search'}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Invited By</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInvitations.map((invitation) => (
                      <TableRow key={invitation.id} hover>
                        <TableCell>{invitation.invitedEmail}</TableCell>
                        <TableCell>{invitation.invitedByEmail}</TableCell>
                        <TableCell>
                          {new Date(invitation.expiresAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Resend invitation">
                            <IconButton
                              onClick={() => handleResendInvite(invitation.id)}
                              disabled={resendLoading}
                              size="small"
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* Invite Member Dialog */}
        <Dialog
          open={inviteDialogOpen}
          onClose={() => {
            setInviteDialogOpen(false);
            setInviteEmail('');
          }}
        >
          <DialogTitle>Invite Member</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter the email address of the person you want to invite to this workspace.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
              sx={{ minWidth: 400 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setInviteDialogOpen(false);
                setInviteEmail('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              variant="contained"
              disabled={inviteLoading || !inviteEmail.trim()}
            >
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove Member Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Remove Member?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to remove <strong>{memberToRemove?.email}</strong> from this
              workspace? They will lose access to all shared items.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRemoveConfirm}
              color="error"
              variant="contained"
              disabled={removeLoading}
            >
              {removeLoading ? 'Removing...' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invitation Link Dialog */}
        <Dialog
          open={inviteLinkDialogOpen}
          onClose={() => setInviteLinkDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Invitation Sent</DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              Invitation email sent! You can also share this link directly.
            </Alert>
            <TextField
              fullWidth
              value={inviteLink}
              InputProps={{ readOnly: true }}
              size="small"
              label="Invitation Link"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This link expires in 7 days. The recipient must sign in with the invited email
              address.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteLinkDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                navigator.clipboard.writeText(inviteLink);
                setSuccess('Link copied to clipboard!');
              }}
            >
              Copy Link
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
          message={success}
        />
      </Container>
    </Box>
  );
}
